import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_ACCESS_ROLES } from '../constants/siteAccessRoles';
import { MRP_METRICS, calcActivityScore } from '../utils/mrpStatisticsMetrics';
import { managersStatsPath } from '../utils/statisticsUrlParams';
import './Overview.css';

const getToken = () => localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const ROLE_LABELS = {
  admin: 'Admin',
  'Chief Manager': 'Chief Manager',
  'Deputy Chief Manager': 'Deputy Chief Manager',
  'Senior Manager': 'Senior Manager',
};

const getHighestSiteRole = (roles) => {
  if (!Array.isArray(roles) || !roles.length) return null;
  for (const role of SITE_ACCESS_ROLES) {
    if (roles.includes(role)) return ROLE_LABELS[role] || role;
  }
  return roles[0];
};

const formatHeaderDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const weekday = date.toLocaleDateString('ru-RU', { weekday: 'short' });
  const dayMonth = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  return `${weekday}, ${dayMonth}`;
};

const formatHeaderTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const getActivityLevel = (score, avg) => {
  if (score <= 0) return 'critical';
  if (avg > 0 && score < avg * 0.35) return 'critical';
  if (avg > 0 && score < avg * 0.6) return 'attention';
  if (avg <= 0 && score < 5) return 'attention';
  return 'ok';
};

const CALL_TYPES = {
  general_department: 'Общий созвон отделения - начало месяца',
  management_team: 'Созвон: Management Team',
  general_department_2: 'Общий созвон отделения - конец месяца',
};

const REMOVED_CALL_TYPES = new Set(['curator_mrp', 'curator_junior_od', 'curator_media']);

const getWeekStartDate = () => {
  const today = new Date();
  const day = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
  monday.setHours(0, 0, 0, 0);
  const y = monday.getFullYear();
  const m = String(monday.getMonth() + 1).padStart(2, '0');
  const d = String(monday.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return dateStr;
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.getTime() === today.getTime()) return 'Сегодня';
  if (d.getTime() === tomorrow.getTime()) return 'Завтра';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
};

const formatDateForServer = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Overview = ({ username, getGreeting, currentTime, user, userRoles }) => {
  const navigate = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState(null);

  const [weekStats, setWeekStats] = useState([]);
  const [loadingWeek, setLoadingWeek] = useState(true);

  const [pendingPunishments, setPendingPunishments] = useState([]);
  const [loadingPunishments, setLoadingPunishments] = useState(true);

  const [activeReprimands, setActiveReprimands] = useState([]);
  const [loadingReprimands, setLoadingReprimands] = useState(true);

  const [vacationsToday, setVacationsToday] = useState([]);
  const [vacationsTomorrow, setVacationsTomorrow] = useState([]);
  const [loadingVacations, setLoadingVacations] = useState(true);

  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadAvatar = async () => {
      try {
        const response = await fetch('/api/auth/discord-avatar', { headers: authHeaders() });
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          if (data.avatarUrl) {
            setAvatarUrl(data.avatarUrl);
            return;
          }
        }
      } catch {
        /* fallback below */
      }
      if (cancelled) return;
      const discriminator = user?.discordDiscriminator || '0';
      const defaultAvatarIndex = parseInt(discriminator, 10) % 5 || 0;
      setAvatarUrl(`https://cdn.discordapp.com/embed/avatars/${defaultAvatarIndex}.png`);
    };
    loadAvatar();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingWeek(true);
      try {
        const response = await fetch(
          `/api/statistics/week/all?weekStartDate=${encodeURIComponent(getWeekStartDate())}`,
          { headers: authHeaders() }
        );
        if (!response.ok) throw new Error('week');
        const data = await response.json();
        if (!cancelled) setWeekStats(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setWeekStats([]);
      } finally {
        if (!cancelled) setLoadingWeek(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingPunishments(true);
      try {
        const response = await fetch('/api/punishments?status=pending', {
          headers: authHeaders(),
        });
        if (!response.ok) throw new Error('punishments');
        const data = await response.json();
        if (!cancelled) setPendingPunishments(Array.isArray(data) ? data : data.items || []);
      } catch {
        if (!cancelled) setPendingPunishments([]);
      } finally {
        if (!cancelled) setLoadingPunishments(false);
      }
    };
    load();
    const interval = setInterval(load, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingReprimands(true);
      try {
        const response = await fetch('/api/reprimands', {
          headers: authHeaders(),
        });
        if (!response.ok) throw new Error('reprimands');
        const data = await response.json();
        if (!cancelled) setActiveReprimands(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setActiveReprimands([]);
      } finally {
        if (!cancelled) setLoadingReprimands(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingVacations(true);
      try {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const todayStr = formatDateForServer(today);
        const tomorrowStr = formatDateForServer(tomorrow);

        const [todayResponse, tomorrowResponse] = await Promise.all([
          fetch(`/api/vacations?startDate=${todayStr}&endDate=${todayStr}`, { headers: authHeaders() }),
          fetch(`/api/vacations?startDate=${tomorrowStr}&endDate=${tomorrowStr}`, { headers: authHeaders() }),
        ]);

        if (!cancelled) {
          setVacationsToday(todayResponse.ok ? await todayResponse.json() : []);
          setVacationsTomorrow(tomorrowResponse.ok ? await tomorrowResponse.json() : []);
        }
      } catch {
        if (!cancelled) {
          setVacationsToday([]);
          setVacationsTomorrow([]);
        }
      } finally {
        if (!cancelled) setLoadingVacations(false);
      }
    };
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingCalls(true);
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setDate(end.getDate() + 7);
        const fmt = (d) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const from = fmt(today);
        const to = fmt(end);

        const rangeKeys = [];
        for (let d = new Date(today); d.getTime() <= end.getTime(); d.setDate(d.getDate() + 1)) {
          rangeKeys.push(fmt(d));
        }
        const rangeMmDd = new Set(rangeKeys.map((k) => k.slice(5)));

        const monthSet = new Set(rangeKeys.map((k) => parseInt(k.slice(5, 7), 10)));
        const birthdayFetches =
          monthSet.size === 1
            ? [fetch(`/api/auth/birthdays?month=${[...monthSet][0]}`, { headers: authHeaders() })]
            : [fetch('/api/auth/birthdays', { headers: authHeaders() })];

        const [callsRes, eventsRes, bdayRes] = await Promise.all([
          fetch(`/api/calls-schedule?startDate=${from}&endDate=${to}`, { headers: authHeaders() }),
          fetch(`/api/calendar-events?startDate=${from}&endDate=${to}`, { headers: authHeaders() }),
          ...birthdayFetches,
        ]);

        const callsRaw = callsRes.ok ? await callsRes.json() : [];
        const eventsRaw = eventsRes.ok ? await eventsRes.json() : [];
        const birthdaysRaw = bdayRes.ok ? await bdayRes.json() : [];

        const calls = (Array.isArray(callsRaw) ? callsRaw : [])
          .filter((c) => !REMOVED_CALL_TYPES.has(c.call_type))
          .map((c) => ({
            _type: 'call',
            date: String(c.call_date || '').slice(0, 10),
            time: c.call_time || '',
            label: c.call_type,
          }));

        const events = (Array.isArray(eventsRaw) ? eventsRaw : []).map((e) => ({
          _type: 'event',
          date: String(e.event_date || '').slice(0, 10),
          time: e.event_time || '',
          label: e.title || 'Событие',
        }));

        const birthdays = (Array.isArray(birthdaysRaw) ? birthdaysRaw : [])
          .filter((b) => b.birthDate && rangeMmDd.has(String(b.birthDate).slice(5)))
          .map((b) => {
            const md = String(b.birthDate).slice(5);
            const date = rangeKeys.find((k) => k.slice(5) === md) || `${from.slice(0, 4)}-${md}`;
            return {
              _type: 'birthday',
              date,
              time: '',
              label: b.discordUsername || 'День рождения',
            };
          });

        const merged = [...calls, ...events, ...birthdays].sort((a, b) => {
          const da = a.date || '';
          const db = b.date || '';
          if (da !== db) return da.localeCompare(db);
          return String(a.time || '').localeCompare(String(b.time || ''));
        });

        if (!cancelled) setUpcomingEvents(merged.slice(0, 6));
      } catch {
        if (!cancelled) setUpcomingEvents([]);
      } finally {
        if (!cancelled) setLoadingCalls(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const weekAvgScore = useMemo(() => {
    if (!weekStats.length) return 0;
    const sum = weekStats.reduce((acc, row) => acc + calcActivityScore(row.totalMetrics || row), 0);
    return Math.round(sum / weekStats.length);
  }, [weekStats]);

  const attentionList = useMemo(() => {
    return weekStats
      .map((row) => {
        const metrics = row.totalMetrics || {};
        const score = calcActivityScore(metrics);
        const level = getActivityLevel(score, weekAvgScore);
        const vsAvg = weekAvgScore > 0 ? Math.round((score / weekAvgScore) * 100) : 0;
        return {
          ...row,
          _metrics: metrics,
          _score: score,
          _level: level,
          _vsAvg: vsAvg,
        };
      })
      .filter((row) => row._level === 'critical' || row._level === 'attention')
      .sort((a, b) => a._score - b._score);
  }, [weekStats, weekAvgScore]);

  const displayName = username || 'Пользователь';
  const greeting = typeof getGreeting === 'function' ? getGreeting() : 'Здравствуйте';
  const highestRole = getHighestSiteRole(userRoles);
  const now = currentTime instanceof Date ? currentTime : new Date();

  const go = (path) => () => navigate(path);

  const vacationPreview = useMemo(() => {
    const today = vacationsToday.slice(0, 4).map((v) => ({ ...v, _when: 'today' }));
    const tomorrow = vacationsTomorrow.slice(0, 4).map((v) => ({ ...v, _when: 'tomorrow' }));
    return [...today, ...tomorrow].slice(0, 6);
  }, [vacationsToday, vacationsTomorrow]);

  return (
    <div className="overview-hub">
      <header className="overview-hub__header">
        <div className="overview-hub__identity">
          <div className="overview-hub__avatar" aria-hidden="true">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" />
            ) : (
              <span>{displayName.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="overview-hub__text">
            <h2 className="overview-hub__title">
              {greeting}, <span>{displayName}</span>
            </h2>
            {highestRole && (
              <div className="overview-hub__meta">
                <span className="overview-hub__role">{highestRole}</span>
              </div>
            )}
          </div>
        </div>

        <div className="overview-hub__aside">
          <div className="overview-hub__clock">
            <span className="overview-hub__date">{formatHeaderDate(now)}</span>
            <span className="overview-hub__time">{formatHeaderTime(now)}</span>
          </div>
        </div>
      </header>

      <div className="stats-grid stats-grid-3">
        <button type="button" className="stat-card overview-kpi" onClick={go('/discordmoderation/punishments')}>
          <div className="stat-icon stat-icon-orange">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <div className="stat-value">{loadingPunishments ? '...' : pendingPunishments.length}</div>
          <div className="stat-label">На проверке</div>
        </button>

        <button type="button" className="stat-card overview-kpi" onClick={go('/discordmoderation/managers')}>
          <div className="stat-icon stat-icon-pink">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="stat-value">{loadingReprimands ? '...' : activeReprimands.length}</div>
          <div className="stat-label">Активные выговоры</div>
        </button>

        <button type="button" className="stat-card overview-kpi" onClick={go('/discordmoderation/vacations')}>
          <div className="stat-icon stat-icon-teal">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="stat-value">{loadingVacations ? '...' : vacationsToday.length}</div>
          <div className="stat-label">В отпуске сегодня</div>
        </button>
      </div>

      <div className="section-header-main section-header-main-spaced">
        <h2 className="section-title-main">ОЧЕРЕДИ</h2>
      </div>

      <div className="content-sections">
        <div className="content-section">
          <div className="section-header">
            <div className="section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <span>Очередь наказаний</span>
            </div>
            <div className="section-action">
              <button className="section-link" onClick={go('/discordmoderation/punishments')}>
                <span>Перейти</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
          <div className="section-content">
            {loadingPunishments ? (
              <div className="section-empty">Загрузка...</div>
            ) : pendingPunishments.length === 0 ? (
              <div className="section-empty">Очередь пуста</div>
            ) : (
              <div className="ov-list">
                {pendingPunishments.slice(0, 6).map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className="ov-row"
                    onClick={go('/discordmoderation/punishments')}
                  >
                    <div className="ov-row__main">
                      <span className="ov-row__title">{p.command || 'Наказание'}</span>
                      <span className="ov-row__meta">
                        {p.target_id || p.reason || 'Без цели'}
                        {p.moderator_nickname ? ` · ${p.moderator_nickname}` : ''}
                      </span>
                    </div>
                    {p.created_at ? (
                      <span className="ov-row__side">
                        {formatDateDisplay(String(p.created_at).slice(0, 10))}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="content-section">
          <div className="section-header">
            <div className="section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" />
                <line x1="22" y1="11" x2="16" y2="11" />
              </svg>
              <span>На контроле</span>
            </div>
            <div className="section-action">
              <button
                className="section-link"
                onClick={go(managersStatsPath({ weekStartStr: getWeekStartDate() }))}
              >
                <span>Перейти</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
          <div className="section-content">
            {loadingWeek ? (
              <div className="section-empty">Загрузка...</div>
            ) : attentionList.length === 0 ? (
              <div className="section-empty">Слабой активности нет</div>
            ) : (
              <div className="ov-list ov-list--control">
                {attentionList.slice(0, 8).map((row) => {
                  const barPct = Math.max(4, Math.min(100, row._vsAvg || 0));
                  return (
                    <button
                      type="button"
                      key={row.discord_id || row.nickname}
                      className="ov-control"
                      onClick={() => {
                        if (!row.discord_id) return;
                        navigate(
                          managersStatsPath({
                            moderatorId: row.discord_id,
                            weekStartStr: getWeekStartDate(),
                          })
                        );
                      }}
                    >
                      <div className="ov-control__top">
                        <div className="ov-control__who">
                          <span className="ov-control__name">{row.nickname || 'Неизвестно'}</span>
                          {(row.server || row.role) && (
                            <span className="ov-control__sub">
                              {[row.server, row.role].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </div>
                        <span className={`ov-chip ov-chip--${row._level}`}>
                          {row._level === 'critical' ? 'Критично' : 'Внимание'}
                        </span>
                      </div>

                      <div className="ov-control__bar" aria-hidden="true">
                        <span className={`ov-control__bar-fill ov-control__bar-fill--${row._level}`} style={{ width: `${barPct}%` }} />
                      </div>

                      <div className="ov-control__activity">
                        <span className="ov-stat__label">Общая активность</span>
                        <span className="ov-stat__value">
                          {row._score}
                          <span className="ov-stat__muted"> / ср. {weekAvgScore}</span>
                        </span>
                      </div>

                      <div className="ov-control__stats">
                        {MRP_METRICS.map((metric) => (
                          <div className="ov-stat" key={metric.key} title={metric.label}>
                            <span className="ov-stat__label">{metric.short}</span>
                            <span className="ov-stat__value">
                              {Number(row._metrics?.[metric.key]) || 0}
                            </span>
                          </div>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="section-header-main section-header-main-spaced">
        <h2 className="section-title-main">БЛИЖАЙШЕЕ</h2>
      </div>

      <div className="content-sections">
        <div className="content-section">
          <div className="section-header">
            <div className="section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span>Отпуска</span>
            </div>
            <div className="section-action">
              <button className="section-link" onClick={go('/discordmoderation/vacations')}>
                <span>Перейти</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
          <div className="section-content">
            {loadingVacations ? (
              <div className="section-empty">Загрузка...</div>
            ) : vacationPreview.length === 0 ? (
              <div className="section-empty">На сегодня и завтра отпусков нет</div>
            ) : (
              <div className="ov-list">
                {vacationPreview.map((vacation) => (
                  <button
                    type="button"
                    key={`${vacation._when}-${vacation.id}`}
                    className="ov-row"
                    onClick={go('/discordmoderation/vacations')}
                  >
                    <div className="ov-row__main">
                      <span className="ov-row__title">{vacation.moderator_nickname || 'Неизвестно'}</span>
                      <span className="ov-row__meta">{vacation.moderator_server || '—'}</span>
                    </div>
                    <span className={`ov-chip ov-chip--${vacation._when}`}>
                      {vacation._when === 'today' ? 'Сегодня' : 'Завтра'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="content-section">
          <div className="section-header">
            <div className="section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
                <path d="M8 14h.01" />
                <path d="M12 14h.01" />
                <path d="M16 14h.01" />
              </svg>
              <span>События</span>
            </div>
            <div className="section-action">
              <button className="section-link" onClick={go('/discordmoderation/calendar')}>
                <span>Перейти</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
          <div className="section-content">
            {loadingCalls ? (
              <div className="section-empty">Загрузка...</div>
            ) : upcomingEvents.length === 0 ? (
              <div className="section-empty">Нет событий на ближайшую неделю</div>
            ) : (
              <div className="ov-list">
                {upcomingEvents.map((item, index) => {
                  const timeStr = item.time ? String(item.time).substring(0, 5) : '';
                  let title;
                  let kind;
                  if (item._type === 'call') {
                    title = CALL_TYPES[item.label] || item.label;
                    kind = 'call';
                  } else if (item._type === 'event') {
                    title = item.label;
                    kind = 'event';
                  } else {
                    title = item.label;
                    kind = 'birthday';
                  }

                  return (
                    <button
                      type="button"
                      key={`${item._type}-${index}`}
                      className="ov-row"
                      onClick={go('/discordmoderation/calendar')}
                    >
                      <span className={`ov-dot ov-dot--${kind}`} aria-hidden="true" />
                      <div className="ov-row__main">
                        <span className="ov-row__title">{title}</span>
                        <span className="ov-row__meta">
                          {item._type === 'birthday' ? 'День рождения' : kind === 'call' ? 'Созвон' : 'Событие'}
                        </span>
                      </div>
                      <span className="ov-row__side">
                        {formatDateDisplay(item.date)}
                        {timeStr ? ` · ${timeStr}` : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
